// src/components/UserDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  collection,
  query,
  where,
  getDocs,
  doc,
} from 'firebase/firestore';
import { sendEmailVerification } from 'firebase/auth';
import { userDoc } from '../utils/firestoreRefs';          // NEW helper import
import { trackPurchase } from '../utils/facebookPixel';

function UserDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isLinkingJourney, setIsLinkingJourney] = useState(false);
  const [linkedSessionIds, setLinkedSessionIds] = useState(new Set());

  const [journeyIds, setJourneyIds] = useState([]);
  const [journeyDocs, setJourneyDocs] = useState([]);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [verificationSending, setVerificationSending] = useState(false);
  const [isAdminOrCreator, setIsAdminOrCreator] = useState(false);
  const [roleCheckComplete, setRoleCheckComplete] = useState(false);

  // ====================
  // Listen for auth changes and check role
  // ====================
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      setAuthChecked(true);

      if (user) {
        setEmailVerified(user.emailVerified);
        setPhoneNumber(user.phoneNumber || null);

        // Check if user is admin or creator
        try {
          // Check admin
          const adminEmails = [
            'matthew@backpass.co',
            'mgriff7@gmail.com',
            'mhgbackpass@gmail.com',
            'mhgcommercial@gmail.com'
          ];
          let isAdmin = adminEmails.includes(user.email);

          // Check user doc for admin flag
          if (!isAdmin) {
            const userDocSnap = await getDoc(doc(db, 'users', user.uid));
            if (userDocSnap.exists() && userDocSnap.data().isAdmin === true) {
              isAdmin = true;
            }
          }

          // Check creator
          let isCreator = false;
          const userDocSnap2 = await getDoc(doc(db, 'users', user.uid));
          if (userDocSnap2.exists() && userDocSnap2.data().isCreator === true) {
            isCreator = true;
          }

          setIsAdminOrCreator(isAdmin || isCreator);
        } catch (error) {
          console.error('Error checking user role:', error);
          setIsAdminOrCreator(false);
        }
      } else {
        setEmailVerified(false);
        setPhoneNumber(null);
        setIsAdminOrCreator(false);
      }
      setRoleCheckComplete(true);
    });
    return () => unsub();
  }, [navigate]);

  // ====================
  // Load Firestore user doc once we know currentUser
  // ====================
  useEffect(() => {
    if (!authChecked || !roleCheckComplete) return;

    if (!currentUser) {
      navigate('/signin');
      return;
    }

    // Redirect regular users to mobile redirect page
    if (!isAdminOrCreator) {
      console.log('Regular user accessing /dashboard, redirecting to mobile app page');
      navigate('/mobile-redirect');
      return;
    }

    const userRef = userDoc(currentUser.uid);              // UID-keyed
    getDoc(userRef).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setJourneyIds(data.journeys || []);

        // ⬆️  NEW — hydrate phone number from Firestore profile if Auth user lacks one
        if (!currentUser.phoneNumber && data.phoneNumber) {
          setPhoneNumber(data.phoneNumber);
        }
      }
    });
  }, [authChecked, roleCheckComplete, currentUser, isAdminOrCreator, navigate]);

  // ====================
  // Fetch journey docs when IDs change
  // ====================
  useEffect(() => {
    if (journeyIds.length === 0) {
      setJourneyDocs([]);
      return;
    }

    async function fetchJourneys() {
      try {
        const q = query(
          collection(db, 'journeys'),
          where('__name__', 'in', journeyIds)
        );
        const qsnap = await getDocs(q);
        const docsData = qsnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setJourneyDocs(docsData);
      } catch (err) {
        console.error(err);
      }
    }

    fetchJourneys();
  }, [journeyIds]);

  // ====================
  // Handle payment success - link journey from session_id
  // ====================
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || !currentUser || !authChecked || isLinkingJourney || linkedSessionIds.has(sessionId)) {
      console.log('⏹️ Skipping journey linking:', { 
        sessionId: !!sessionId, 
        currentUser: !!currentUser, 
        authChecked, 
        isLinkingJourney,
        alreadyLinked: linkedSessionIds.has(sessionId)
      });
      return;
    }

    async function linkJourneyFromSession() {
      setIsLinkingJourney(true);
      try {
        console.log('🔗 Starting journey linking process for session:', sessionId);
        
        // Verify the session and get journey data
        console.log('📡 Verifying session with backend...');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verify-session/${sessionId}`);
        if (!response.ok) {
          console.error('❌ Session verification failed:', response.status, response.statusText);
          return;
        }

        const sessionData = await response.json();
        console.log('✅ Session data received:', sessionData);
        if (!sessionData.valid) {
          console.error('❌ Invalid session:', sessionData);
          return;
        }

        // Find the journey by code with retry logic for webhook race condition
        let journeyDoc = null;
        let retryCount = 0;
        const maxRetries = 5;
        const retryDelay = 2000; // 2 seconds

        while (retryCount < maxRetries && !journeyDoc) {
          try {
            const journeyQuery = query(
              collection(db, 'journeys'),
              where('code', '==', sessionData.journeyCode)
            );
            const querySnapshot = await getDocs(journeyQuery);

            if (!querySnapshot.empty) {
              journeyDoc = querySnapshot.docs[0];
              console.log('Found journey for code:', sessionData.journeyCode);
              break;
            } else {
              // Journey not found yet - webhook still processing
              console.log(`Journey not found yet for code ${sessionData.journeyCode}, retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
              retryCount++;
              if (retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
              }
            }
          } catch (fetchError) {
            console.error('Error fetching journey:', fetchError);
            retryCount++;
            if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
          }
        }

        if (!journeyDoc) {
          console.error('Journey not found after all retries for code:', sessionData.journeyCode);
          return;
        }

        const journeyId = journeyDoc.id;
        const journeyData = journeyDoc.data();

        // Check if journey is already linked to this user
        if (journeyData.userId === currentUser.uid) {
          console.log('Journey already linked to user:', journeyId);
          // Remove session_id from URL since we're done
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
          return;
        }

        // Update user document to include this journey (same pattern as LinkJourneyCode)
        console.log('👤 Updating user document to include journey...');
        const userRef = userDoc(currentUser.uid);
        try {
          const userSnap = await getDoc(userRef);
          if (!userSnap.exists()) {
            // Create user doc if it doesn't exist
            await setDoc(userRef, {
              journeys: [journeyId],
            });
          } else {
            // Use setDoc with merge (same as LinkJourneyCode) instead of updateDoc
            await setDoc(
              userRef,
              { journeys: arrayUnion(journeyId) },
              { merge: true }
            );
          }
          console.log('✅ User document updated successfully');
        } catch (userUpdateError) {
          console.error('❌ Failed to update user document:', userUpdateError);
          throw userUpdateError;
        }

        // Update the journey to link it to the user (but handle permission errors gracefully)
        console.log('📝 Updating journey document to link to user...');
        try {
          await updateDoc(journeyDoc.ref, {
            userId: currentUser.uid,
            userEmail: currentUser.email
          });
          console.log('✅ Journey document updated successfully');
        } catch (journeyUpdateError) {
          console.warn('⚠️ Failed to update journey document (but user linking succeeded):', journeyUpdateError);
          // Don't throw here - user linking succeeded, journey linking failed but that's optional
        }

        console.log('🎉 Successfully linked journey:', journeyId);

        // Track Facebook Pixel Purchase event
        console.log('🟢 [DASHBOARD DEBUG] About to track Purchase event...');
        console.log('🟢 [DASHBOARD DEBUG] Journey data:', journeyData);
        try {
          const purchaseParams = {
            value: journeyData.cost || journeyData.price || 0,
            currency: journeyData.currency || 'USD',
            routeName: journeyData.name || 'BackPass Journey',
            routeId: journeyData.routeId || journeyId,
            transactionId: journeyId,
            startDate: journeyData.startDate || '',
            endDate: journeyData.endDate || '',
            nights: journeyData.nights || 0,
          };
          console.log('🟢 [DASHBOARD DEBUG] Purchase params:', purchaseParams);
          console.log('🟢 [DASHBOARD DEBUG] Calling trackPurchase()...');
          trackPurchase(purchaseParams);
          console.log('🟢 [DASHBOARD DEBUG] trackPurchase() call completed');
        } catch (pixelError) {
          console.error('❌ [DASHBOARD DEBUG] Failed to track purchase:', pixelError);
          console.error('Error details:', pixelError.stack);
          // Don't throw - pixel tracking failure shouldn't break the flow
        }

        // Mark this session as successfully linked
        setLinkedSessionIds(prev => new Set(prev).add(sessionId));

        // Remove the session_id from URL
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        
        // Refresh the journey list
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setJourneyIds(userSnap.data().journeys || []);
        }

      } catch (error) {
        console.error('💥 Critical error linking journey from session:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Full error:', error);
        
        // Show user-friendly message if it's a permission error
        if (error.code === 'permission-denied') {
          console.error('🚫 Permission denied - this suggests Firestore security rules are blocking the update');
          alert('Access Denied Error - Link Code First');
        }
      } finally {
        setIsLinkingJourney(false);
      }
    }

    linkJourneyFromSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentUser, authChecked]); // Don't include isLinkingJourney in deps

  // ====================
  // Send verification email
  // ====================
  async function handleSendVerificationEmail() {
    if (!currentUser) return;
    setVerificationSending(true);
    try {
      await sendEmailVerification(currentUser);
      alert('Verification email sent. Please check your inbox.');
    } catch (err) {
      console.error('Error sending verification email:', err);
      alert(`Failed to send verification email: ${err.message}`);
    }
    setVerificationSending(false);
  }

  // ====================
  // Link a new Journey Code
  // ====================
  async function handleLinkCode(e) {
    e.preventDefault();
    setError('');
    setStatus('');

    if (!currentUser) {
      setError('You must be logged in to link a journey code.');
      return;
    }

    try {
      const journeysRef = collection(db, 'journeys');
      const codeQuery   = query(journeysRef, where('code', '==', code));
      const querySnap   = await getDocs(codeQuery);

      if (querySnap.empty) {
        setError('Invalid journey code. Please try again.');
        return;
      }

      const journeyDoc = querySnap.docs[0];
      const journeyId  = journeyDoc.id;

      const userRef = userDoc(currentUser.uid);            // UID-keyed
      await updateDoc(userRef, {
        journeys: arrayUnion(journeyId),
      });

      setJourneyIds((prev) => [...prev, journeyId]);
      setStatus(`Successfully linked journey: ${journeyId}`);
      setCode('');
    } catch (err) {
      console.error('Error linking journey code:', err);
      setError(err.message);
    }
  }

  // ====================
  // Logout
  // ====================
  function handleLogout() {
    auth.signOut().then(() => {
      navigate('/signin');
    });
  }

  // ====================
  // Render
  // ====================
  if (!authChecked) {
    return (
      <div style={{ padding: 20, color: 'white', textAlign: 'center' }}>
        <h2>Loading...</h2>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: '800px',
        margin: '10px auto',
        backgroundColor: 'rgba(255, 255, 255, 0.0)',
        color: 'white',
        padding: '20px',
        borderRadius: '0px',
      }}
    >
      <h1
        style={{
          marginBottom: '15px',
          marginTop: '0px',
          textAlign: 'center',
          color: '#853a38',
          fontSize: '1.75rem',
        }}
      >
        User Dashboard
      </h1>

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {status && <p style={{ color: 'lightgreen' }}>{status}</p>}

      {/* Personal Information */}
      <div
        style={{
          marginBottom: '1em',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '1em',
          borderRadius: '8px',
        }}
      >
        <h3>Personal Information</h3>
        <p>View or update your info, verify phone/email, and upload ID docs.</p>
        <button
          onClick={() => navigate('/user-info')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#853a38',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Manage My Info
        </button>
      </div>

      {/* Verification Status */}
      <div
        style={{
          marginBottom: '1em',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '1em',
          borderRadius: '8px',
        }}
      >
        <h3>Verification Status</h3>
        <p>Email: {emailVerified ? 'Verified' : 'Not Verified'}</p>
        <p>
          Phone:{' '}
          {phoneNumber ? `Verified (${phoneNumber})` : 'No phone number on file'}
        </p>
        {!emailVerified && (
          <div style={{ marginTop: '1em' }}>
            <button
              onClick={handleSendVerificationEmail}
              disabled={verificationSending}
              style={{
                padding: '8px 12px',
                backgroundColor: '#853a38',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: verificationSending ? 'not-allowed' : 'pointer',
              }}
            >
              {verificationSending
                ? 'Sending verification email...'
                : 'Send Verification Email'}
            </button>
          </div>
        )}
        {!phoneNumber && (
          <div style={{ marginTop: '1em' }}>
            <button
              onClick={() => navigate('/add-phone')}
              style={{
                padding: '8px 12px',
                backgroundColor: '#006400',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Add Phone Number
            </button>
          </div>
        )}
      </div>

      {/* Link Code Section */}
      <div
        style={{
          marginBottom: '1em',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '1em',
          borderRadius: '8px',
        }}
      >
        <h3>Link a Journey Code</h3>
        <form onSubmit={handleLinkCode} style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter your journey code"
            required
            style={{ flex: 1, padding: '8px' }}
          />
          <button
            type="submit"
            style={{
              padding: '8px 12px',
              backgroundColor: '#853a38',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Link Code
          </button>
        </form>
      </div>

      {/* Journeys Section */}
      <div
        style={{
          marginBottom: '1em',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '1em',
          borderRadius: '8px',
        }}
      >
        <h3>My Journeys</h3>
        {journeyDocs.length === 0 ? (
          <>
            <p>No journeys linked yet.</p>
            <button
              onClick={() => navigate('/store')}
              style={{
                padding: '8px 12px',
                backgroundColor: '#006400',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Buy a Pass
            </button>
          </>
        ) : (
          <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
            {journeyDocs.map((journey) => (
              <li key={journey.id} style={{ marginBottom: '0.5em' }}>
                <strong>{journey.name || journey.id}</strong> – Code:{' '}
                {journey.code}{' '}
                <button
                  onClick={() => navigate(`/journey/${journey.id}`)}
                  style={{
                    marginLeft: '10px',
                    padding: '6px 12px',
                    backgroundColor: '#853a38',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  View Journey
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Memories / Achievements */}
      <div
        style={{
          marginBottom: '1em',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '1em',
          borderRadius: '8px',
        }}
      >
        <h3>Memories & Achievements</h3>
        <p>Coming soon... or click below to see the new page!</p>
        <button
          onClick={() => navigate('/memories')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#853a38',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Go to Memories
        </button>
      </div>

      {/* Store / Upgrades Stub */}
      <div
        style={{
          marginBottom: '1em',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '1em',
          borderRadius: '8px',
        }}
      >
        <h3>Store / Upgrades</h3>
        <p>Coming soon... or click below to see the new page!</p>
        <button
          onClick={() => navigate('/store')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#853a38',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Go to Store
        </button>
      </div>

      {/* Password Reset */}
      <div
        style={{
          marginBottom: '1em',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '1em',
          borderRadius: '8px',
        }}
      >
        <h3>Password Reset</h3>
        <p>If you need to reset your password, click below.</p>
        <button
          onClick={() => navigate('/password-reset')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#1e90ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Reset Password
        </button>
      </div>

      {/* Logout */}
      <div style={{ marginTop: '1em' }}>
        <button
          onClick={handleLogout}
          style={{
            padding: '8px 12px',
            backgroundColor: '#800020',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Logout
        </button>
      </div>

      {/* Back to Home */}
      <div style={{ marginTop: '1em' }}>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#444',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Back to Home
        </button>
      </div>

      {/* Account Deletion */}
      <div
        style={{
          marginTop: '2em',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '1em',
          borderRadius: '8px',
        }}
      >
        <h3>Account Deletion</h3>
        <p>oof ouch owie.</p>
        <button
          onClick={() => navigate('/delete-account')}
          style={{
            padding: '8px 12px',
            backgroundColor: '#a52a2a',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          (Don't) Push me.
        </button>
      </div>
    </div>
  );
}

export default UserDashboard;
